import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { url } from "../../utils/config";
import { serverMessage } from "../../utils/serverMessage";
import { delayMessage } from "../../utils/delayMessage";

interface AllPostJobsState {
  allPostJobs: Object[];
  engineeringPostJobs: Object[];
  resetJobsArray: Object[];
  resetEngrJobsArray: Object[];
  postJobsLoading: boolean;
  pendingRequestTimer: any;
  postJobsCount: number;
  searchJob: string;
  isShowJobType: boolean;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

const initialState: AllPostJobsState = {
  allPostJobs: [],
  engineeringPostJobs: [],
  resetJobsArray: [],
  resetEngrJobsArray: [],
  postJobsLoading: false,
  pendingRequestTimer: null,
  postJobsCount: 0,
  searchJob: "",
  isShowJobType: false,
  currentPage: 1,
  pageSize: 20,
  totalPages: 0,
};

export const getAllPostJobs = createAsyncThunk(
  "allPostJobs",
  async (details: any, thunkAPI) => {
    const { isEngineering, currentPage, pageSize } = details;
    try {
      const { data } = await axios.get(
        `${url.baseUrl}/postJob?page=${currentPage}&size=${pageSize}`
      );
      // console.log(data);

      return { data, isEngineering };
    } catch (error: any) {
      console.log(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const getAllPostJobsSlice = createSlice({
  name: "allPostJobs",
  initialState,
  reducers: {
    collectInputField: (state, { payload }) => {
      state.searchJob = payload;
    },
    toggleJobTypeDropdown: (state) => {
      state.isShowJobType = !state.isShowJobType;
    },
    hideJobTypeDropdown: (state) => {
      state.isShowJobType = false;
    },
    clearSearchField: (state) => {
      state.searchJob = initialState.searchJob;
      state.allPostJobs = state.resetJobsArray;
      state.engineeringPostJobs = state.resetEngrJobsArray;
    },

    searchForPostJobs: (state, { payload }) => {
      // const { search, jobType } = payload;
      const { search = "", jobType = "" } = payload;

      let filteredJobs = state.resetJobsArray.slice();

      // Filter by input search
      filteredJobs = filteredJobs.filter((each: any) => {
        return (
          each.jobLocation.toLowerCase().includes(search.toLowerCase()) ||
          each.position.toLowerCase().includes(search.toLowerCase())
        );
      });

      // Filter based on job type
      filteredJobs = filteredJobs.filter((each: any) => {
        return each.jobType.toLowerCase().includes(jobType.toLowerCase());
      });

      state.allPostJobs = filteredJobs;
    },
    searchForEngeeringPostJobs: (state, { payload }) => {
      const { search = "", jobType = "" } = payload;

      let filteredJobs = state.resetEngrJobsArray.slice();

      // Filter by input search
      filteredJobs = filteredJobs.filter((each: any) => {
        return (
          each.jobLocation.toLowerCase().includes(search.toLowerCase()) ||
          each.position.toLowerCase().includes(search.toLowerCase())
        );
      });

      // Filter based on job type
      filteredJobs = filteredJobs.filter((each: any) => {
        return each.jobType.toLowerCase().includes(jobType.toLowerCase());
      });

      state.engineeringPostJobs = filteredJobs;
    },

    increasePage: (state) => {
      state.currentPage = state.currentPage + 1;
    },
    decreasePage: (state) => {
      state.currentPage = state.currentPage - 1;
    },
    jobsPerPage: (state, { payload }) => {
      state.pageSize = payload;
    },
    // Feature coming soon
    // jumpToPage: (state, { payload }) => {
    //   state.currentPage = payload;
    // },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getAllPostJobs.pending, (state) => {
        state.postJobsLoading = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
        }
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(getAllPostJobs.fulfilled, (state, { payload }) => {
        const { data, isEngineering } = payload;

        state.postJobsLoading = false;
        state.resetJobsArray = data.data.postJobsList;
        state.postJobsCount = data.data.allJobsCount;
        state.totalPages = data.data.totalPages;

        const engineeringJobs = data.data?.postJobsList?.filter((each: any) => {
          return (
            each.position.toLowerCase().includes("engineer") ||
            each.position.toLowerCase().includes("software") ||
            each.position.toLowerCase().includes("developer") ||
            each.position.toLowerCase().includes("design") ||
            each.company.toLowerCase().includes("engineer") ||
            each.company.toLowerCase().includes("tech") ||
            each.company.toLowerCase().includes("software") ||
            each.company.toLowerCase().includes("design") ||
            each.jobDescription.toLowerCase().includes("engineer") ||
            each.jobDescription.toLowerCase().includes("tech") ||
            each.jobDescription.toLowerCase().includes("software") ||
            each.jobDescription.toLowerCase().includes("design") ||
            each.jobRequirements.toLowerCase().includes("engineer") ||
            each.jobRequirements.toLowerCase().includes("tech") ||
            each.jobRequirements.toLowerCase().includes("software") ||
            each.jobRequirements.toLowerCase().includes("design") ||
            each.interviewProcess.toLowerCase().includes("engineer") ||
            each.interviewProcess.toLowerCase().includes("tech") ||
            each.interviewProcess.toLowerCase().includes("software") ||
            each.interviewProcess.toLowerCase().includes("design")
          );
        });

        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }

        // Return only engineering jobs if on engineering jobs page
        if (isEngineering) {
          state.engineeringPostJobs = engineeringJobs;
          state.resetEngrJobsArray = engineeringJobs;
          return;
        }

        // Return all jobs if on all post jobs page
        state.allPostJobs = data.data.postJobsList;

        // console.log(engineeringJobs);
      })
      .addCase(getAllPostJobs.rejected, (state, { payload }: any) => {
        state.postJobsLoading = false;
        serverMessage(payload); // Server error
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
      });
  },
});

export default getAllPostJobsSlice.reducer;

export const {
  collectInputField,
  searchForPostJobs,
  searchForEngeeringPostJobs,
  toggleJobTypeDropdown,
  hideJobTypeDropdown,
  clearSearchField,
  increasePage,
  decreasePage,
  jobsPerPage,
} = getAllPostJobsSlice.actions;
